// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  ShieldBan,
  Plus,
  Users,
  CalendarDays,
  Clock,
  Mail,
  Cake,
  StickyNote,
  CalendarCheck,
} from 'lucide-react';
import { useReservation } from '../../contexts/ReservationContext.tsx';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import ReservationForm from '../../components/admin/ReservationForm.tsx';
import { fetchBloqueios, BloqueioResponse, fetchRestaurantSettings } from '../../services/api.ts';
import { toast } from 'react-toastify';
import { buildWhatsappLink, WHATSAPP_ICON_URL } from '../../../../../utils/whatsapp';
import '../../styles/reservation-calendar.css';

const getTurnoNome = (turno?: number | null) => {
  if (turno === 0) return 'Almoço';
  if (turno === 1) return 'Jantar';
  return 'Turno';
};

const formatBirthdate = (isoDate?: string | null) => {
  if (!isoDate) return '';
  const [datePart] = isoDate.split('T');
  const parts = datePart?.split('-');
  if (!parts || parts.length !== 3) return isoDate;
  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return isoDate;
  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const parseTimeToMinutes = (time?: string | null) => {
  if (!time) return Number.MAX_SAFE_INTEGER;
  const [hoursStr, minutesStr = '0'] = time.split(':');
  const hours = Number.parseInt(hoursStr, 10);
  const minutes = Number.parseInt(minutesStr, 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return hours * 60 + minutes;
};

const STATUS_LABELS: Record<string, string> = {
  pendente: 'Pendente',
  confirmada: 'Confirmada',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
  no_show: 'No-show'
};

const normalizeStatusValue = (status?: string | null) => {
  const normalized = status?.toLowerCase?.().trim?.() || '';
  if (!normalized) return '';
  if (normalized === 'confirmado') return 'confirmada';
  if (normalized === 'cancelada' || normalized === 'cancelado') return 'cancelada';
  if (normalized === 'finalizado' || normalized === 'finalizada') return 'concluida';
  if (normalized === 'concluido' || normalized === 'concluída' || normalized === 'concluida') return 'concluida';
  if (normalized === 'no show' || normalized === 'no-show' || normalized === 'noshow'|| normalized === 'no_show') return 'no_show';
  return normalized;
};

const isCanceledReservation = (reservation: any) =>
  normalizeStatusValue(reservation?.status) === 'cancelada';

const resolveDisplayStatus = (status: string, requiresConfirmation: boolean) => {
  if (!requiresConfirmation && !['cancelada', 'concluida', 'no_show'].includes(status)) {
    return 'confirmada';
  }
  return status;
};

const ReservationCalendar: React.FC = () => {
  const { reservations, updateReservation } = useReservation();
  const { floors } = useRestaurant();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [blocks, setBlocks] = useState<BloqueioResponse[]>([]);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [restaurantSettings, setRestaurantSettings] = useState<any>({});

  const loadBlocks = async () => {
    try {
      const data = await fetchBloqueios();
      setBlocks(data || []);
    } catch (error) {
      console.error('Erro ao carregar bloqueios:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const data = await fetchRestaurantSettings();
      setRestaurantSettings(data || {});
    } catch (error) {
      console.error('Erro ao carregar configurações do restaurante:', error);
    }
  };

  useEffect(() => {
    loadBlocks();
    loadSettings();
  }, []);

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const navigateMonth = (direction: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const handleDayClick = (day: number) => {
    setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
  };

  const handleNewReservation = () => {
    setSelectedReservation(null);
    setShowReservationForm(true);
  };

  const handleEditReservation = (reservation: any) => {
    setSelectedReservation(reservation);
    setShowReservationForm(true);
  };

  const handleCloseForm = () => {
    setShowReservationForm(false);
    setSelectedReservation(null);
  };

  const getReservationsForDay = (day: number) => {
    const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return reservations
      .filter((res) => {
        if (!res.data || typeof res.data !== 'string') return false;
        const [year, month, parsedDay] = res.data.split('-').map(Number);
        const resDate = new Date(year, month - 1, parsedDay);
        return (
          resDate.getDate() === targetDate.getDate() &&
          resDate.getMonth() === targetDate.getMonth() &&
          resDate.getFullYear() === targetDate.getFullYear()
        );
      })
      .sort((a, b) => parseTimeToMinutes(a?.hora) - parseTimeToMinutes(b?.hora));
  };

  const selectedDayReservations = getReservationsForDay(selectedDate.getDate());

  const monthlyStats = useMemo(() => {
    const validReservations = reservations.filter((res) => {
      if (!res?.data || typeof res.data !== 'string') return false;
      const [year, month] = res.data.split('-').map(Number);
      return (
        year === currentDate.getFullYear() &&
        month === currentDate.getMonth() + 1 &&
        !isCanceledReservation(res)
      );
    });
    const totalPeople = validReservations.reduce((sum, res) => {
      const n = Number(res?.numero_pessoas);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
    const occupiedDays = new Set(validReservations.map((r) => r.data)).size;
    return {
      totalReservations: validReservations.length,
      totalPeople,
      occupiedDays,
    };
  }, [reservations, currentDate]);

  const goToToday = () => {
    const now = new Date();
    setCurrentDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDate(now);
  };

  const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  const isToday = (day: number) => {
    const now = new Date();
    return (
      day === now.getDate() &&
      currentDate.getMonth() === now.getMonth() &&
      currentDate.getFullYear() === now.getFullYear()
    );
  };

  const getInitials = (name?: string | null) => {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const selectedDayActiveReservations = selectedDayReservations.filter(
    (r) => !isCanceledReservation(r),
  );
  const selectedDayPeople = selectedDayActiveReservations.reduce((sum, r) => {
    const n = Number(r?.numero_pessoas);
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);

  const formatDateKey = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const isFutureOrToday = (iso: string) => {
    const compare = new Date(`${iso}T23:59:59`);
    return compare >= today;
  };

  const selectedDayBlocks = useMemo(() => {
    const target = formatDateKey(selectedDate);
    return blocks.filter((block) => block.data === target && isFutureOrToday(block.data));
    // Omite `isFutureOrToday`/`today`: ancorados no dia corrente do mount da tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, selectedDate]);

  const requiresRestaurantConfirmation = Boolean(restaurantSettings?.reservo_confirmacao_restaurante);

  const getTableInfo = (
    mesa_id: number | string | null | undefined,
    mesa_ids: Array<number | string> | null | undefined,
    sala_id: number | string | null | undefined
  ) => {
    const normalizedMesaIds = Array.isArray(mesa_ids) && mesa_ids.length > 0
      ? mesa_ids
      : (mesa_id !== null && mesa_id !== undefined ? [mesa_id] : []);
    const floor = sala_id !== null && sala_id !== undefined
      ? floors.find((f) => String(f.id) === String(sala_id))
      : undefined;

    if (!floor) {
      return {
        tableName: normalizedMesaIds.length > 0
          ? normalizedMesaIds.map((currentMesaId) => `Mesa ${currentMesaId}`).join(', ')
          : 'Mesa não definida',
        floorName: 'Sala desconhecida'
      };
    }

    if (normalizedMesaIds.length === 0) {
      return {
        tableName: '',
        floorName: floor.name
      };
    }

    return {
      tableName: normalizedMesaIds.map((currentMesaId) => {
        const table = floor.tables.find((t) => String(t.id) === String(currentMesaId));
        return table ? table.name : `Mesa ${currentMesaId}`;
      }).join(', '),
      floorName: floor.name
    };
  };

  const handleStatusChange = async (
    reservationId: string | number,
    nextStatus: string
  ) => {
    if (!reservationId) return;
    setStatusUpdatingId(String(reservationId));
    try {
      await updateReservation(reservationId, { status: nextStatus });
      toast.success('Status atualizado com sucesso.');
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Não foi possível atualizar o status.');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  return (
    <div className="reservation-calendar">
      <div className="calendar-header">
        <div className="calendar-header-text">
          <h1>Calendário de Reservas</h1>
          <p className="calendar-subtitle">
            Visualize, organize e gerencie as reservas do seu restaurante mês a mês.
          </p>
        </div>
        <div className="calendar-actions">
          <button className="btn btn-primary" onClick={handleNewReservation}>
            <Plus size={16} />
            Nova Reserva
          </button>
        </div>
      </div>

      <div className="calendar-stats">
        <div className="stat-card stat-card-primary">
          <div className="stat-icon stat-icon-primary">
            <CalendarCheck size={22} />
          </div>
          <div className="stat-body">
            <span className="stat-label">Reservas no mês</span>
            <strong className="stat-value">{monthlyStats.totalReservations}</strong>
          </div>
        </div>
        <div className="stat-card stat-card-people">
          <div className="stat-icon stat-icon-people">
            <Users size={22} />
          </div>
          <div className="stat-body">
            <span className="stat-label">Pessoas previstas</span>
            <strong className="stat-value">{monthlyStats.totalPeople}</strong>
          </div>
        </div>
        <div className="stat-card stat-card-days">
          <div className="stat-icon stat-icon-days">
            <CalendarDays size={22} />
          </div>
          <div className="stat-body">
            <span className="stat-label">Dias com reservas</span>
            <strong className="stat-value">{monthlyStats.occupiedDays}</strong>
          </div>
        </div>
      </div>

      <div className="calendar-container">
        <div className="month-navigation">
          <button
            type="button"
            className="month-nav-btn"
            onClick={() => navigateMonth(-1)}
            aria-label="Mês anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <h2>
            {capitalize(currentDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' }))}
          </h2>
          <div className="month-nav-actions">
            <button type="button" className="btn-today" onClick={goToToday}>
              Hoje
            </button>
            <button
              type="button"
              className="month-nav-btn"
              onClick={() => navigateMonth(1)}
              aria-label="Próximo mês"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="calendar-grid">
          <div className="weekday-header">
            <div>Dom</div>
            <div>Seg</div>
            <div>Ter</div>
            <div>Qua</div>
            <div>Qui</div>
            <div>Sex</div>
            <div>Sáb</div>
          </div>

          <div className="days-grid">
            {Array.from({ length: firstDayOfMonth }).map((_, index) => (
              <div key={`empty-${index}`} className="day empty"></div>
            ))}

            {Array.from({ length: daysInMonth }).map((_, index) => {
              const day = index + 1;
              const dayReservations = getReservationsForDay(day);
              const calendarReservations = dayReservations.filter((reservation) => !isCanceledReservation(reservation));
              const totalPeople = calendarReservations.reduce((sum, reservation) => {
                const peopleCount = Number(reservation?.numero_pessoas);
                return Number.isFinite(peopleCount) ? sum + peopleCount : sum;
              }, 0);
              const dateKey = formatDateKey(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
              const dayBlocks = blocks.filter((block) => block.data === dateKey && isFutureOrToday(block.data));
              const isSelected =
                selectedDate.getDate() === day &&
                selectedDate.getMonth() === currentDate.getMonth() &&
                selectedDate.getFullYear() === currentDate.getFullYear();
              const isTodayCell = isToday(day);
              const reservationsLabel = `${calendarReservations.length} ${calendarReservations.length === 1 ? 'reserva' : 'reservas'}`;
              const peopleLabel = `${totalPeople} ${totalPeople === 1 ? 'pessoa' : 'pessoas'}`;

              return (
                <div
                  key={day}
                  className={`day ${isSelected ? 'selected' : ''} ${isTodayCell ? 'today' : ''} ${calendarReservations.length > 0 ? 'has-reservations' : ''}`}
                  onClick={() => handleDayClick(day)}
                >
                  <div className="day-number">
                    {day}
                    {isTodayCell && <span className="today-dot" aria-hidden="true" />}
                  </div>
                  {calendarReservations.length > 0 && (
                    <div className="day-indicators">
                      <div className="reservation-indicator reservation-count">
                        {reservationsLabel}
                      </div>
                      <div className="reservation-indicator people-count">
                        {peopleLabel}
                      </div>
                    </div>
                  )}
                  {dayBlocks.length > 0 && (
                    <div className="block-indicator">
                      {dayBlocks.length} bloqueio{dayBlocks.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="day-schedule">
        <div className="day-schedule-header">
          <h3>
            <CalendarIcon size={18} />
            {capitalize(selectedDate.toLocaleDateString('pt-BR', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            }))}
          </h3>
          {selectedDayActiveReservations.length > 0 && (
            <div className="day-schedule-summary">
              <span className="day-schedule-chip">
                <CalendarCheck size={13} aria-hidden="true" />
                {selectedDayActiveReservations.length} {selectedDayActiveReservations.length === 1 ? 'reserva' : 'reservas'}
              </span>
              <span className="day-schedule-chip">
                <Users size={13} aria-hidden="true" />
                {selectedDayPeople} {selectedDayPeople === 1 ? 'pessoa' : 'pessoas'}
              </span>
            </div>
          )}
        </div>

        {selectedDayBlocks.length > 0 && (
          <div className="day-blocks">
            <h4>
              <ShieldBan size={16} /> Horários bloqueados
            </h4>
            <ul>
              {selectedDayBlocks.map((block) => {
                const turnoLabel = getTurnoNome(block.turno);
                const sala = floors.find((f) => f.id === block.sala_id)?.name || 'Sala desconhecida';
                const intervalo = block.hora_inicio && block.hora_fim
                  ? `${block.hora_inicio.slice(0, 5)} \u2013 ${block.hora_fim.slice(0, 5)}`
                  : null;

                return (
                  <li key={block.id}>
                    <span className="block-turno">{turnoLabel}</span>
                    <span className="block-sala">{sala}</span>
                    {intervalo && <span className="block-intervalo">{intervalo}</span>}
                    {block.motivo && <small>{block.motivo}</small>}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {selectedDayReservations.length > 0 ? (
          <div className="reservations-list">
            {selectedDayReservations.map((reservation, index) => {
              const { tableName, floorName } = getTableInfo(reservation.mesa_id, reservation.mesa_ids, reservation.sala_id);
              const normalizedStatus = normalizeStatusValue(reservation.status);
              const effectiveStatus = normalizedStatus || (requiresRestaurantConfirmation ? 'pendente' : 'confirmada');
              const displayStatus = resolveDisplayStatus(effectiveStatus, requiresRestaurantConfirmation);
              const statusLabel = STATUS_LABELS[displayStatus] || displayStatus;
              const canConfirm = requiresRestaurantConfirmation && effectiveStatus === 'pendente';
              const whatsappLink = buildWhatsappLink(reservation.telefone_cliente);

              const peopleCountLabel = `${reservation.numero_pessoas} ${Number(reservation.numero_pessoas) === 1 ? 'pessoa' : 'pessoas'}`;
              const initials = getInitials(reservation.nome_cliente);
              const shortTime = (reservation.hora || '').slice(0, 5);
              return (
                <div
                  key={index}
                  className={`reservation-card status-${displayStatus}`}
                  onClick={() => handleEditReservation(reservation)}
                >
                  <div className="reservation-time">
                    <Clock size={13} aria-hidden="true" />
                    <span>{shortTime || reservation.hora}</span>
                  </div>
                  <div className="reservation-details">
                    <div className="reservation-header-row">
                      <div className="reservation-customer">
                        <div className={`reservation-avatar avatar-${displayStatus}`}>
                          {initials}
                        </div>
                        <h4>{reservation.nome_cliente}</h4>
                      </div>
                      <div
                        className="reservation-status-control"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <span className={`reservation-status-badge status-${displayStatus}`}>
                          {statusLabel}
                        </span>
                        {canConfirm && (
                          <button
                            type="button"
                            className="reservation-status-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleStatusChange(`${reservation.id}`, 'confirmada');
                            }}
                            disabled={statusUpdatingId === String(reservation.id)}
                          >
                            {statusUpdatingId === String(reservation.id) ? 'Confirmando\u2026' : 'Confirmar'}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="reservation-meta">
                      <span className="reservation-meta-item">
                        <Users size={13} aria-hidden="true" />
                        {peopleCountLabel}
                      </span>
                      <span className="reservation-meta-divider" aria-hidden="true">•</span>
                      <span className="reservation-meta-item">
                        {floorName}{tableName ? ` · ${tableName}` : ''}
                      </span>
                    </div>
                    {(reservation.telefone_cliente || reservation.email_cliente || reservation.nascimento_cliente) && (
                      <div className="reservation-contact-grid">
                        {reservation.telefone_cliente && (
                          <span className="reservation-contact-item">
                            {reservation.telefone_cliente}
                          </span>
                        )}
                        {reservation.email_cliente && (
                          <span className="reservation-contact-item">
                            <Mail size={12} aria-hidden="true" />
                            {reservation.email_cliente}
                          </span>
                        )}
                        {reservation.nascimento_cliente && (
                          <span className="reservation-contact-item">
                            <Cake size={12} aria-hidden="true" />
                            {formatBirthdate(reservation.nascimento_cliente)}
                          </span>
                        )}
                        {whatsappLink && (
                          <a
                            className="reservation-whatsapp-link"
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                            aria-label={`Falar com ${reservation.nome_cliente || 'cliente'} pelo WhatsApp`}
                          >
                            <img
                              className="reservation-whatsapp-icon"
                              src={WHATSAPP_ICON_URL}
                              alt=""
                              aria-hidden="true"
                            />
                            <span>Falar com cliente</span>
                          </a>
                        )}
                      </div>
                    )}
                    {reservation.obs && (
                      <p className="reservation-notes">
                        <StickyNote size={12} aria-hidden="true" />
                        {reservation.obs}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-reservations">
            <div className="no-reservations-icon">
              <CalendarIcon size={28} aria-hidden="true" />
            </div>
            <p className="no-reservations-title">Nenhuma reserva para este dia</p>
            <p className="no-reservations-hint">
              Clique em "Nova Reserva" para registrar a primeira reserva desta data.
            </p>
          </div>
        )}
      </div>

      {showReservationForm && (
        <ReservationForm
          reservation={selectedReservation}
          onClose={handleCloseForm}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
};

export default ReservationCalendar;
